import React from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { ResourceDto } from "../types/resources";
import "../styles/resources.css";

type Props = {
    resource: ResourceDto;
    onLaunch?: (r: ResourceDto) => void;
    onExpertClick?: (r: ResourceDto) => void;
};

export default function ResourceCard({ resource, onLaunch, onExpertClick }: Props){
    const navigate = useNavigate();

    const moduleCount = resource.modules?.length ?? 0;
    const hasModules = moduleCount > 0;

    const goToDetail = () => {
        navigate(`/resources/${resource.slug}`);
    };

    const triggerLaunch = () => {
        if (onLaunch) {
            onLaunch(resource);
        }
    };

    const handleCardClick = () => {
        if (hasModules) {
            goToDetail();
        } else {
            triggerLaunch();
        }
    };

    const handleCardKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
        }
    };

    const handleLaunchClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasModules) {
            goToDetail();
        } else {
            triggerLaunch();
        }
    };

    const handleExpertClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onExpertClick && resource.expert) {
            onExpertClick(resource);
        }
    };

    const sanitizedExcerpt = resource.excerpt
        ? DOMPurify.sanitize(resource.excerpt)
        : "";

    return (
        <article
            className="rcard"
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleCardKeyDown}
            data-testid={`resource-card-${resource.slug}`}
        >
            <div className="rcard-media">
                {resource.featuredImageUrl ? (
                    <img src={resource.featuredImageUrl} alt={resource.title} />
                ) : (
                    <img src="data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 10'%3E%3Crect width='16' height='10' fill='%23e5e7eb'/%3E%3C/svg%3E" alt="" />
                )}
            </div>

            <div className="rcard-body">
                {resource.expert && (
                    <div className="rcard-expert-row">
                        <div className="rcard-expert-info">
                            <div className="rcard-expert-avatar">
                                <img
                                    src={resource.expert.expertImageUrl}
                                    alt={resource.expert.title}
                                />
                            </div>
                            <div className="rcard-expert-details">
                                <button
                                    className="rcard-expert-name-link"
                                    onClick={handleExpertClick}
                                >
                                    {resource.expert.title}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <h3 className="rcard-title">{resource.title}</h3>
                {resource.subtitle && <p className="rcard-subtitle">{resource.subtitle}</p>}
                {sanitizedExcerpt && (
                    <div
                        className="rcard-excerpt"
                        dangerouslySetInnerHTML={{ __html: sanitizedExcerpt }}
                    />
                )}

                {(hasModules || resource.label || resource.lengthOfCourse || resource.isCompleted) && (
                    <div className="rcard-meta-row">
                        {hasModules && (
                            <span className="rcard-meta-chip">
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                >
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                                </svg>
                                {moduleCount} {moduleCount === 1 ? 'Module' : 'Modules'}
                            </span>
                        )}
                        {resource.label && (
                            <span className="rcard-label-pill">{resource.label}</span>
                        )}
                        {resource.lengthOfCourse && (
                            <span
                                className="rcard-meta-chip"
                                data-testid={`resource-card-length-${resource.slug}`}
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                >
                                    <circle cx="12" cy="12" r="9" />
                                    <polyline points="12 7 12 12 15 14" />
                                </svg>
                                {resource.lengthOfCourse}
                            </span>
                        )}
                        {resource.isCompleted && (
                            <span
                                className="rcard-completed-pill"
                                data-testid={`resource-card-completed-${resource.slug}`}
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Completed
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="rcard-actions">
                <button className="rcard-launch" onClick={handleLaunchClick}>
                    {resource.buttonLabel}
                </button>
            </div>
        </article>
    );
}
